import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import { trans } from '@/mixins/i18n.js';
import { router } from '@inertiajs/vue3';
import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import { route } from 'ziggy-js';

export type WorkOrderLaneStatus = 'to_do' | 'in_progress' | 'done';

type WorkOrderId = string | number;

type WorkOrderLaneMeta = {
    total: number;
    next_cursor: string | null;
};

type WorkOrderAssetInfo = {
    operability: EnumResource<string>;
};

type EnumResource<T> = {
    value: T;
    label: string;
};

type WorkOrderPriority = EnumResource<string>;

export type WorkOrderLanePayload<T> = {
    data: T[];
    meta: WorkOrderLaneMeta;
};

type WorkOrderSummary = {
    id: WorkOrderId;
    status?: EnumResource<WorkOrderLaneStatus>;
    priority: WorkOrderPriority;
    asset: WorkOrderAssetInfo;
    updatedAt?: string;
    [key: string]: unknown;
};

type WorkOrderFull = WorkOrderSummary & Record<string, unknown>;

type LaneState<T> = {
    items: T[];
    meta: WorkOrderLaneMeta;
    isLoading: boolean;
};

type ArchivedWorkOrder = {
    id: WorkOrderId;
    [key: string]: unknown;
};

const emptyMeta = (): WorkOrderLaneMeta => ({ total: 0, next_cursor: null });

function dedupeById<T extends { id: WorkOrderId }>(items: T[]): T[] {
    const seen = new Set<WorkOrderId>();
    const result: T[] = [];
    for (const item of items) {
        if (!item?.id || seen.has(item.id)) continue;
        seen.add(item.id);
        result.push(item);
    }
    return result;
}

export const useWorkOrderStore = defineStore('workOrders', () => {
    const lanes = ref<Record<WorkOrderLaneStatus, LaneState<WorkOrderSummary>>>({
        to_do: { items: [], meta: emptyMeta(), isLoading: false },
        in_progress: { items: [], meta: emptyMeta(), isLoading: false },
        done: { items: [], meta: emptyMeta(), isLoading: false },
    });

    const archived = ref<LaneState<ArchivedWorkOrder>>({ items: [], meta: emptyMeta(), isLoading: false });

    const byId = ref<Record<string, WorkOrderFull>>({});

    const highlightedWorkOrderId = ref<WorkOrderId | null>(null);
    const processingIds = ref<Set<WorkOrderId>>(new Set());
    let queryParamsSource: Ref<Record<string, unknown> | null> | null = null;
    const explicitQueryParams = ref<Record<string, unknown> | null>(null);

    const isProcessing = computed(
        () =>
            (id: WorkOrderId): boolean =>
                processingIds.value.has(id),
    );

    const setWorkOrderLoading = (id: WorkOrderId, processing: boolean): void => {
        if (!id) return;
        if (processing) {
            processingIds.value.add(id);
        } else {
            processingIds.value.delete(id);
        }
        // ensure reactivity
        processingIds.value = new Set(processingIds.value);
    };

    const hydrateLane = (status: WorkOrderLaneStatus, payload: unknown): void => {
        const incoming = payload as WorkOrderLanePayload<WorkOrderSummary> | null | undefined;
        if (!incoming || !Array.isArray(incoming.data) || !incoming.meta) {
            lanes.value[status] = { items: [], meta: emptyMeta(), isLoading: false };
            return;
        }

        const items = dedupeById(incoming.data);
        lanes.value[status] = {
            items,
            meta: {
                total: typeof incoming.meta.total === 'number' ? incoming.meta.total : items.length,
                next_cursor: incoming.meta.next_cursor ?? null,
            },
            isLoading: false,
        };

        items.forEach((wo) => {
            if (!wo?.id) return;
            byId.value[String(wo.id)] = { ...(byId.value[String(wo.id)] ?? {}), ...wo };
        });
        byId.value = { ...byId.value };
    };

    const hydrateArchived = (payload: unknown, mode: 'replace' | 'append' = 'replace'): void => {
        const incoming = payload as WorkOrderLanePayload<ArchivedWorkOrder> | null | undefined;
        if (!incoming || !Array.isArray(incoming.data) || !incoming.meta) {
            archived.value = { items: [], meta: emptyMeta(), isLoading: false };
            return;
        }

        const incomingItems = dedupeById(incoming.data);

        if (mode === 'replace') {
            archived.value = {
                items: incomingItems,
                meta: {
                    total: typeof incoming.meta.total === 'number' ? incoming.meta.total : incomingItems.length,
                    next_cursor: incoming.meta.next_cursor ?? null,
                },
                isLoading: false,
            };
            return;
        }

        const existingIds = new Set((archived.value.items ?? []).map((w) => String(w.id)));
        const nextItems = incomingItems.filter((w) => w?.id && !existingIds.has(String(w.id)));

        archived.value = {
            items: [...archived.value.items, ...nextItems],
            meta: {
                total: typeof incoming.meta.total === 'number' ? incoming.meta.total : archived.value.meta.total,
                next_cursor: incoming.meta.next_cursor ?? null,
            },
            isLoading: false,
        };
    };

    const setLaneLoading = (status: WorkOrderLaneStatus, isLoading: boolean): void => {
        const lane = lanes.value[status];
        lanes.value[status] = { ...lane, isLoading };
    };

    const setAllLanesLoading = (isLoading: boolean): void => {
        (Object.keys(lanes.value) as WorkOrderLaneStatus[]).forEach((status) => {
            setLaneLoading(status, isLoading);
        });
    };

    const setQueryParamsSourceRef = (source: Ref<Record<string, unknown> | null> | null): void => {
        queryParamsSource = source;
    };

    const reset = () => {
        explicitQueryParams.value = null;
        queryParamsSource = null;
        highlightedWorkOrderId.value = null;
        processingIds.value = new Set();
        lanes.value = {
            to_do: { items: [], meta: emptyMeta(), isLoading: false },
            in_progress: { items: [], meta: emptyMeta(), isLoading: false },
            done: { items: [], meta: emptyMeta(), isLoading: false },
        };
        archived.value = { items: [], meta: emptyMeta(), isLoading: false };
    };

    const setQueryParams = (params: Record<string, unknown> | null): void => {
        explicitQueryParams.value = params ? { ...params } : null;
    };

    const getEffectiveQueryParams = (): Record<string, unknown> => {
        if (explicitQueryParams.value && typeof explicitQueryParams.value === 'object') {
            const { page: _page, cursor: _cursor, ...params } = explicitQueryParams.value;
            return { ...params };
        }

        const data = (queryParamsSource?.value as any)?.form?.data?.() ?? null;
        if (!data || typeof data !== 'object') {
            return {};
        }

        const { page: _page, cursor: _cursor, ...params } = data as Record<string, unknown>;
        return { ...params };
    };

    const upsert = (workOrder: WorkOrderFull): void => {
        if (!workOrder?.id) return;
        const key = String(workOrder.id);
        byId.value[key] = { ...(byId.value[key] ?? {}), ...workOrder };
        byId.value = { ...byId.value };

        // Replace in-place within any lane that currently contains it.
        (Object.keys(lanes.value) as WorkOrderLaneStatus[]).forEach((status) => {
            const idx = lanes.value[status].items.findIndex((w) => String(w.id) === key);
            if (idx === -1) return;
            const next = [...lanes.value[status].items];
            next[idx] = byId.value[key];
            lanes.value[status] = { ...lanes.value[status], items: next };
        });
    };

    const reloadWorkOrder = async (id: WorkOrderId): Promise<void> => {
        if (!id) return;

        const resp = await $http.get(route('workorders.show', String(id)), {
            headers: { Accept: 'application/json' },
        });

        const workOrder = resp?.data?.workOrder as WorkOrderFull;

        if (!workOrder) return;
        upsert(workOrder);
    };

    const removeWorkOrder = (workOrderId: WorkOrderId): void => {
        if (!workOrderId) return;
        const key = String(workOrderId);

        // Remove from all lanes and adjust totals optimistically.
        (Object.keys(lanes.value) as WorkOrderLaneStatus[]).forEach((status) => {
            const before = lanes.value[status].items;
            const after = before.filter((w) => String(w.id) !== key);
            if (after.length === before.length) {
                return;
            }

            const nextTotal = Math.max(0, (lanes.value[status].meta?.total ?? 0) - 1);
            lanes.value[status] = {
                ...lanes.value[status],
                items: after,
                meta: { ...lanes.value[status].meta, total: nextTotal },
            };
        });

        if (highlightedWorkOrderId.value && String(highlightedWorkOrderId.value) === key) {
            highlightedWorkOrderId.value = null;
        }

        delete byId.value[key];
        byId.value = { ...byId.value };
    };

    const reloadArchived = async (): Promise<void> => {
        if (archived.value.isLoading) return;

        archived.value = { ...archived.value, isLoading: true };

        try {
            const resp = await $http.get(route('workorders.archived'), {
                params: getEffectiveQueryParams(),
                headers: { Accept: 'application/json' },
            });

            hydrateArchived(resp?.data, 'replace');
        } finally {
            archived.value = { ...archived.value, isLoading: false };
        }
    };

    const loadMoreArchived = async (): Promise<void> => {
        if (archived.value.isLoading) return;
        if (!archived.value.meta.next_cursor) return;

        archived.value = { ...archived.value, isLoading: true };

        try {
            const resp = await $http.get(route('workorders.archived'), {
                params: { ...getEffectiveQueryParams(), cursor: archived.value.meta.next_cursor },
                headers: { Accept: 'application/json' },
            });

            hydrateArchived(resp?.data, 'append');
        } finally {
            archived.value = { ...archived.value, isLoading: false };
        }
    };

    const loadMore = async (status: WorkOrderLaneStatus): Promise<void> => {
        const lane = lanes.value[status];
        if (lane.isLoading) return;
        if (!lane.meta.next_cursor) return;

        lanes.value[status] = { ...lane, isLoading: true };

        try {
            const params = {
                ...getEffectiveQueryParams(),
                status,
                cursor: lane.meta.next_cursor,
            };
            const resp = await $http.get(route('workorders.swimlane'), { params });
            const incoming = resp?.data as WorkOrderLanePayload<WorkOrderSummary> | null | undefined;
            if (!incoming || !Array.isArray(incoming.data) || !incoming.meta) return;

            const existingIds = new Set(lanes.value[status].items.map((w) => String(w.id)));
            const nextItems = (incoming.data ?? []).filter((w) => w?.id && !existingIds.has(String(w.id)));

            nextItems.forEach((wo) => {
                byId.value[String(wo.id)] = { ...(byId.value[String(wo.id)] ?? {}), ...wo };
            });

            byId.value = { ...byId.value };

            lanes.value[status] = {
                items: [...lanes.value[status].items, ...nextItems],
                meta: {
                    total: typeof incoming.meta.total === 'number' ? incoming.meta.total : lanes.value[status].meta.total,
                    next_cursor: incoming.meta.next_cursor ?? null,
                },
                isLoading: false,
            };
        } finally {
            lanes.value[status] = { ...lanes.value[status], isLoading: false };
        }
    };

    const reloadLanes = async (): Promise<void[]> => {
        return Promise.all((Object.keys(lanes.value) as WorkOrderLaneStatus[]).map((status) => reloadLane(status)));
    };

    const reloadLane = async (status: WorkOrderLaneStatus): Promise<void> => {
        const lane = lanes.value[status];
        if (lane.isLoading) return;

        lanes.value[status] = { ...lane, isLoading: true };

        try {
            const params = {
                ...getEffectiveQueryParams(),
                status,
            };

            const resp = await $http.get(route('workorders.swimlane'), {
                params,
                headers: { Accept: 'application/json' },
            });

            const incoming = resp?.data as WorkOrderLanePayload<WorkOrderSummary> | null | undefined;
            hydrateLane(status, incoming);
        } finally {
            lanes.value[status] = { ...lanes.value[status], isLoading: false };
        }
    };

    const updateStatus = (workOrder: WorkOrderSummary, nextStatus: WorkOrderLaneStatus, resolvedDate?: string | null): void => {
        if (!workOrder?.id) return;
        const id = workOrder.id;
        const key = String(id);

        setWorkOrderLoading(id, true);

        const payload: { status: WorkOrderLaneStatus; resolvedDate?: string } = { status: nextStatus };
        if (resolvedDate) {
            payload.resolvedDate = resolvedDate;
        }

        router.put(route('workorders.status.update', id), payload, {
            preserveState: true,
            preserveScroll: true,
            only: ['flash', 'errors'],
            onSuccess: () => {
                eventBus.$emit(events.workOrderStatusUpdated, {
                    workOrderId: key,
                    status: nextStatus,
                });
            },
            onError: (errors: Record<string, string>) => {
                const message = Object.values(errors ?? {})[0] || trans('general.error.unknown');
                eventBus.$emit(events.presentFlashMessage, { payload: { message, status: 'error' } });
            },
            onFinish: () => {
                setWorkOrderLoading(id, false);
            },
        });
    };

    const eventHandlers = {
        assetOperabilityUpdated: () => {
            reloadLanes();
        },
        workOrderStatusUpdated: () => {
            reloadLanes();
        },
        workOrderCreated() {
            reloadLane('to_do');
        },
        workOrderUpdated: (id: WorkOrderId) => {
            reloadWorkOrder(id);
        },
        workOrderDeleted: (id: WorkOrderId) => {
            removeWorkOrder(id);
        },
    };

    const setup = (): void => {
        eventBus.$on(events.assetOperabilityUpdated, eventHandlers.assetOperabilityUpdated);
        eventBus.$on(events.workOrderStatusUpdated, eventHandlers.workOrderStatusUpdated);
        eventBus.$on(events.workOrderCreated, eventHandlers.workOrderCreated);
        eventBus.$on(events.workOrderUpdated, eventHandlers.workOrderUpdated);
        eventBus.$on(events.workOrderDeleted, eventHandlers.workOrderDeleted);
    };

    const dispose = (): void => {
        eventBus.$off(events.assetOperabilityUpdated, eventHandlers.assetOperabilityUpdated);
        eventBus.$off(events.workOrderStatusUpdated, eventHandlers.workOrderStatusUpdated);
        eventBus.$off(events.workOrderCreated, eventHandlers.workOrderCreated);
        eventBus.$off(events.workOrderUpdated, eventHandlers.workOrderUpdated);
        eventBus.$off(events.workOrderDeleted, eventHandlers.workOrderDeleted);
        reset();
    };

    return {
        // state
        lanes,
        archived,
        byId,
        highlightedWorkOrderId,
        // getters
        isProcessing,
        // actions
        setLaneLoading,
        setAllLanesLoading,
        setQueryParamsSourceRef,
        setQueryParams,
        reloadWorkOrder,
        reloadLane,
        reloadArchived,
        upsert,
        removeWorkOrder,
        loadMore,
        loadMoreArchived,
        reloadLanes,
        updateStatus,
        setWorkOrderLoading,
        reset,
        setup,
        dispose,
    };
});
